import { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { LocalizedLink as Link } from '../components/LocalizedLink';
import { useTranslation, Trans } from 'react-i18next';
import { SEO } from '../components/SEO';
import { useLocalizedSeo } from '../hooks/useLocalizedSeo';
import { AnnouncementBanner } from '../components/AnnouncementBanner';
import { PublicHeader } from '../components/PublicHeader';
import { PublicFooter, PlayStoreBadge } from '../components/PublicFooter';
import { isSubdomainEnabled, isOnAppSubdomain, navigateToSite, getAppDomain } from '../utils/domain';
import { usePublicConfig } from '../contexts/PublicConfigContext';
import { bannersApi } from '../services/api';
import { AdSlot } from '../components/AdSlot';
import ScoringFAQ from '../components/ScoringFAQ';
import { usePublicFreeLimits } from '../hooks/usePublicFreeLimits';
import type { BannerConfig, FreeLimits } from '../types';

// ─── JSON-LD Structured Data (Schema.org) ───────────────────────────────────
// Organization + WebSite ficam inline no index.html (necessario para Googlebot
// fase 1 reconhecer o site name "Bolão Elite" no SERP). Aqui ficam apenas os
// schemas especificos da home: SoftwareApplication, ItemList, HowTo, FAQPage.
// Recebe `limits` pra que os numeros do plano free fiquem sincronizados com a
// config do admin (mesma fonte que o FAQ visual e as paginas /premium etc.).
function buildLandingJsonLd(limits: FreeLimits) {
  const created = limits.maxPrivatePoolsCreated;
  const members = limits.maxPrivatePoolMembers;
  const joined = limits.maxPrivatePoolsJoined;
  const poolWord = created === 1 ? 'bolão privado' : 'bolões privados';
  return [
  {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Bolão Elite',
    applicationCategory: 'SportsApplication',
    applicationSubCategory: 'Football / Soccer',
    operatingSystem: 'Web, iOS, Android',
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'BRL', availability: 'https://schema.org/InStock' },
    aggregateRating: { '@type': 'AggregateRating', ratingValue: '5', reviewCount: '1' },
    description:
      'O Bolão Elite é o melhor sistema gratuito de bolão de futebol online do Brasil. Crie bolões para Copa do Mundo FIFA 2026 (EUA, México e Canadá), Campeonato Brasileiro Série A 2026, Copa do Brasil 2026, Copa Libertadores da América 2026, UEFA Champions League 2026, Campeonato Carioca 2026, Campeonato Paulista 2026, Campeonato Gaúcho 2026, Campeonato Mineiro 2026 e outros. Ranking em tempo real, notificações push antes dos jogos, convites por link para WhatsApp e Instagram. 100% gratuito.',
    url: 'https://bolaoelite.com.br',
    keywords: 'bolão de futebol online, bolão Copa do Mundo 2026, criar bolão futebol grátis, bolão futebol com amigos, bolão online gratuito, bolão Brasileirão 2026, bolão Copa do Brasil 2026, bolão Libertadores 2026, bolão Champions League 2026, bolão Carioca 2026, bolão Paulista 2026, bolão Gaúcho 2026, bolão Mineiro 2026, palpites futebol online',
    featureList: [
      'Bolão Copa do Mundo FIFA 2026 — EUA, México e Canadá',
      'Bolão Campeonato Brasileiro Série A 2026 (Brasileirão)',
      'Bolão Copa do Brasil 2026',
      'Bolão Copa Libertadores da América 2026',
      'Bolão Copa Sul-Americana 2026',
      'Bolão UEFA Champions League 2026',
      'Bolão Campeonato Carioca 2026',
      'Bolão Campeonato Paulista 2026 (Paulistão)',
      'Bolão Campeonato Gaúcho 2026',
      'Bolão Campeonato Mineiro 2026',
      'Bolões públicos com premiação — participe sem convite',
      'Bolões privados com link de convite para amigos',
      `Plano gratuito: ${created} ${poolWord} ativo + bolões públicos ilimitados`,
      'Plano Premium: bolões privados ilimitados sem restrições',
      'Ranking em tempo real com critérios de desempate automáticos',
      'Notificações push antes dos jogos',
      'Convites por link via WhatsApp e Instagram',
      'Sistema de pontuação configurável (3 regras)',
      'Participação 100% gratuita',
    ],
    inLanguage: 'pt-BR',
    datePublished: '2026-01-15',
    dateModified: '2026-04-27',
    author: { '@id': 'https://bolaoelite.com.br/#organization' },
    publisher: { '@id': 'https://bolaoelite.com.br/#organization' },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Campeonatos disponíveis para bolão no Bolão Elite',
    description: 'Lista completa de campeonatos de futebol suportados pelo Bolão Elite para criação de bolões online gratuitos.',
    inLanguage: 'pt-BR',
    dateModified: '2026-04-27',
    itemListElement: [
      {
        '@type': 'ListItem', position: 1,
        name: 'Bolão Copa do Mundo FIFA 2026',
        description: 'Crie seu bolão da Copa do Mundo 2026, realizada em junho e julho de 2026 nos Estados Unidos, México e Canadá. O maior evento de futebol do mundo com 48 seleções participantes.',
        url: 'https://bolaoelite.com.br',
      },
      {
        '@type': 'ListItem', position: 2,
        name: 'Bolão Campeonato Brasileiro 2026 (Brasileirão Série A)',
        description: 'Bolão do Campeonato Brasileiro Série A 2026, a principal liga de futebol do Brasil com 20 clubes disputando 38 rodadas.',
        url: 'https://bolaoelite.com.br',
      },
      {
        '@type': 'ListItem', position: 3,
        name: 'Bolão Copa do Brasil 2026',
        description: 'Bolão da Copa do Brasil 2026, torneio eliminatório que reúne clubes de todos os estados brasileiros.',
        url: 'https://bolaoelite.com.br',
      },
      {
        '@type': 'ListItem', position: 4,
        name: 'Bolão Copa Libertadores da América 2026',
        description: 'Bolão da Copa Libertadores 2026, o torneio mais importante do futebol sul-americano.',
        url: 'https://bolaoelite.com.br',
      },
      {
        '@type': 'ListItem', position: 5,
        name: 'Bolão UEFA Champions League 2026',
        description: 'Bolão da Champions League 2026, a maior competição de clubes do futebol mundial.',
        url: 'https://bolaoelite.com.br',
      },
      {
        '@type': 'ListItem', position: 6,
        name: 'Bolão Campeonato Carioca 2026',
        description: 'Bolão do Campeonato Carioca 2026 com Flamengo, Vasco, Botafogo, Fluminense e os demais clubes do Rio de Janeiro.',
        url: 'https://bolaoelite.com.br',
      },
      {
        '@type': 'ListItem', position: 7,
        name: 'Bolão Campeonato Paulista 2026 (Paulistão)',
        description: 'Bolão do Campeonato Paulista 2026 com Corinthians, Palmeiras, Santos, São Paulo e os maiores clubes de SP.',
        url: 'https://bolaoelite.com.br',
      },
      {
        '@type': 'ListItem', position: 8,
        name: 'Bolão Campeonato Gaúcho e Mineiro 2026',
        description: 'Bolões para os campeonatos estaduais de 2026: Gaúcho, Mineiro, Baiano, Pernambucano e outros.',
        url: 'https://bolaoelite.com.br',
      },
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: 'Como participar de um bolão de futebol online grátis',
    description:
      'Passo a passo para criar ou entrar em um bolão de futebol online no Bolão Elite — Copa do Mundo 2026, Brasileirão, estaduais e mais. Totalmente gratuito.',
    inLanguage: 'pt-BR',
    datePublished: '2026-01-15',
    dateModified: '2026-04-27',
    author: { '@id': 'https://bolaoelite.com.br/#organization' },
    publisher: { '@id': 'https://bolaoelite.com.br/#organization' },
    step: [
      {
        '@type': 'HowToStep', position: 1,
        name: 'Crie uma conta gratuita',
        text: "Acesse bolaoelite.com.br e crie sua conta grátis com e-mail ou pelo Google. Em menos de 1 minuto você já está dentro.",
      },
      {
        '@type': 'HowToStep', position: 2,
        name: 'Escolha como participar',
        text: "Opção 1: Crie seu bolão privado — clique em 'Novo Bolão', dê um nome, escolha a competição e convide amigos pelo link. Opção 2: Entre em um bolão público — explore os bolões abertos na plataforma e participe com um clique, sem precisar de convite.",
      },
      {
        '@type': 'HowToStep', position: 3,
        name: 'Faça seus Palpites',
        text: 'Antes do apito inicial de cada partida, registre o placar que você acredita que vai acontecer. Os palpites são bloqueados automaticamente quando o jogo começa.',
      },
      {
        '@type': 'HowToStep', position: 4,
        name: 'Acompanhe o Ranking',
        text: 'Após os jogos, o sistema atualiza automaticamente a pontuação e o ranking de todos os participantes em tempo real.',
      },
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    inLanguage: 'pt-BR',
    datePublished: '2026-01-15',
    dateModified: '2026-04-27',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'O que é o Bolão Elite?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'O Bolão Elite é uma plataforma gratuita de bolão de futebol online. Permite criar bolões para Copa do Mundo 2026, Campeonato Brasileiro (Brasileirão), Copa do Brasil, Libertadores, Champions League, Carioca, Paulista, Gaúcho, Mineiro e outros campeonatos. Os participantes fazem palpites nos placares das partidas e competem em um ranking atualizado em tempo real.',
        },
      },
      {
        '@type': 'Question',
        name: 'O bolão de futebol online é legal no Brasil?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Sim! O Bolão Elite é completamente legal. A plataforma não envolve dinheiro, apostas financeiras ou qualquer forma de ganho monetário. É um jogo recreativo entre amigos, família ou colegas de trabalho, sem intermediação financeira pela plataforma. Qualquer premiação combinada entre os participantes é de responsabilidade deles — o Bolão Elite apenas gerencia os palpites e o ranking.',
        },
      },
      {
        '@type': 'Question',
        name: 'O Bolão Elite tem a Copa do Mundo 2026?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Sim! O Bolão Elite já suporta a Copa do Mundo FIFA 2026, realizada de junho a julho de 2026 nos Estados Unidos, México e Canadá. Crie seu bolão agora e deixe tudo pronto para quando os jogos começarem. Os palpites são bloqueados automaticamente no apito inicial de cada partida.',
        },
      },
      {
        '@type': 'Question',
        name: 'O Bolão Elite é gratuito?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: `O Bolão Elite tem plano gratuito e plano Premium. No plano gratuito você pode criar ${created} ${poolWord} com até ${members} participantes e entrar em até ${joined} bolões privados como convidado, além de bolões públicos ilimitados. O plano Premium remove todas as restrições: bolões privados ilimitados, participantes ilimitados e muito mais.`,
        },
      },
      {
        '@type': 'Question',
        name: 'Como funciona o recurso de ocultar palpites antes de iniciar a partida?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'O criador do bolão pode ativar a opção de ocultar palpites antes do início de cada partida. Com isso, os participantes não conseguem ver os palpites uns dos outros até a partida começar, evitando influências e deixando o bolão mais justo e divertido. A opção pode ser definida ao criar o bolão ou ativada depois pelo menu de configurações do bolão.',
        },
      },
      {
        '@type': 'Question',
        name: 'Quais são os limites do plano gratuito?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: `No plano gratuito você pode criar ${created} ${poolWord} com até ${members} participantes e participar de até ${joined} bolões privados como convidado. Bolões públicos não têm limite de participação. O plano Premium desbloqueia bolões privados ilimitados, participantes ilimitados e recursos exclusivos. Os limites exatos podem ser consultados na plataforma e são atualizados pelo administrador.`,
        },
      },
      {
        '@type': 'Question',
        name: `No plano gratuito só consigo ter ${created} ${poolWord} ao mesmo tempo?`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `Isso mesmo! No plano gratuito você pode ter ${created} ${poolWord} ativo simultaneamente. Se quiser criar um novo bolão para outra competição ou grupo de amigos, basta excluir o bolão anterior — e a vaga fica disponível imediatamente. Quer criar bolões privados ilimitados sem essa limitação? O plano Premium libera tudo isso e muito mais.`,
        },
      },
      {
        '@type': 'Question',
        name: 'Quais campeonatos posso usar no meu bolão?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'O Bolão Elite suporta os principais campeonatos de futebol: Copa do Mundo FIFA 2026, Campeonato Brasileiro Série A (Brasileirão), Copa do Brasil, Copa Libertadores da América, UEFA Champions League, Campeonato Carioca, Campeonato Paulista (Paulistão), Campeonato Gaúcho, Campeonato Mineiro e outros campeonatos estaduais e internacionais.',
        },
      },
      {
        '@type': 'Question',
        name: 'Como criar um bolão da Copa do Mundo 2026?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Para criar um bolão da Copa do Mundo 2026 no Bolão Elite: 1) Crie sua conta gratuita em bolaoelite.com.br; 2) Clique em "Novo Bolão"; 3) Escolha a competição "Copa do Mundo 2026"; 4) Defina o nome e a regra de pontuação; 5) Compartilhe o link de convite com seus amigos via WhatsApp ou Instagram. Pronto! Os palpites serão liberados assim que as partidas forem cadastradas no sistema.',
        },
      },
      {
        '@type': 'Question',
        name: 'O que são bolões públicos no Bolão Elite?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Bolões públicos são bolões abertos a qualquer usuário da plataforma, sem precisar de link de convite. Basta acessar a seção "Bolões Públicos", escolher um bolão e clicar em "Participar". Você compete com outros fãs de futebol da comunidade e pode sair a qualquer momento.',
        },
      },
      {
        '@type': 'Question',
        name: 'Posso participar de vários bolões ao mesmo tempo?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Sim! Você pode criar e participar de múltiplos bolões simultaneamente — bolões privados com seus amigos (Copa do Mundo 2026, Brasileirão, Carioca…) e bolões públicos abertos da comunidade. Cada bolão tem seu próprio ranking e regras.',
        },
      },
      {
        '@type': 'Question',
        name: 'Como funciona a pontuação do bolão?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'O Bolão Elite oferece 3 regras de pontuação. Regra A (simples): placar exato = 1 ponto. Regra B (popular): placar exato = 3 pontos, vencedor correto ou empate = 1 ponto. Regra C (avançada): placar exato = 10 pontos, vencedor = 5 pontos, gols de um time corretos = 2 pontos. A regra é definida pelo criador do bolão.',
        },
      },
      {
        '@type': 'Question',
        name: 'Qual é o melhor site de bolão de futebol grátis?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'O Bolão Elite é uma das melhores opções de bolão de futebol online gratuito no Brasil. Oferece suporte a Copa do Mundo 2026, Brasileirão, Copa do Brasil, Libertadores, Champions League e campeonatos estaduais, com ranking em tempo real, notificações push, convites por link e interface mobile-first. Totalmente gratuito e sem anúncios.',
        },
      },
      {
        '@type': 'Question',
        name: 'Até quando posso fazer meu palpite no bolão?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'O criador do bolão define um tempo de bloqueio antes do início da partida: 10, 30 ou 60 minutos. Após esse prazo, os palpites são bloqueados automaticamente, mesmo que o jogo ainda não tenha começado.',
        },
      },
      {
        '@type': 'Question',
        name: 'Preciso baixar algum aplicativo para usar o Bolão Elite?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Não! O Bolão Elite funciona diretamente no navegador do seu celular ou computador, sem precisar instalar nada. Você também pode adicioná-lo à tela inicial do smartphone (iOS e Android) para acesso rápido, como se fosse um app nativo — é um PWA (Progressive Web App).',
        },
      },
      {
        '@type': 'Question',
        name: 'Como convidar amigos para o bolão?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Cada bolão no Bolão Elite possui um link de convite único. Basta compartilhar esse link pelo WhatsApp, Instagram, Telegram ou qualquer outra rede social. Quem receber o link, cria uma conta gratuita e já entra direto no bolão.',
        },
      },
    ],
  },
  ];
}

// ─── Dados das seções da home — chave i18n (namespace 'landing') + default
// PT-BR. Resolvidos em runtime via t(). ─────────────────────────────────────

const HERO_STATS = [
  { value: '100%', labelKey: 'home.stat_1_label', labelDefault: 'Gratuito' },
  { value: '⚡', labelKey: 'home.stat_2_label', labelDefault: 'Tempo real' },
  { value: '📱', labelKey: 'home.stat_3_label', labelDefault: 'Mobile first' },
];

const COMPETITIONS = [
  { flag: '🌍', href: '/bolao-copa-do-mundo', nameKey: 'home.comp_0_name', name: 'Copa do Mundo 2026', descKey: 'home.comp_0_desc', desc: 'O maior evento de futebol do planeta chega em junho de 2026 nos EUA, México e Canadá. 48 seleções, 104 jogos. Crie seu bolão da Copa do Mundo agora.' },
  { flag: '🇧🇷', href: '/bolao-brasileirao', nameKey: 'home.comp_1_name', name: 'Brasileirão 2026', descKey: 'home.comp_1_desc', desc: 'O Campeonato Brasileiro Série A com 20 clubes e 38 rodadas de pura emoção. Monte um bolão do Brasileirão e dispute cada rodada.' },
  { flag: '🏆', href: '/bolao-copa-do-brasil', nameKey: 'home.comp_2_name', name: 'Copa do Brasil 2026', descKey: 'home.comp_2_desc', desc: 'Torneio eliminatório com clubes de todo o país. Cada jogo pode ser o último — ideal para um bolão emocionante entre amigos.' },
  { flag: '🌎', href: '/bolao-libertadores', nameKey: 'home.comp_3_name', name: 'Copa Libertadores 2026', descKey: 'home.comp_3_desc', desc: 'O torneio mais importante do futebol sul-americano. Crie um bolão da Libertadores e acompanhe os maiores clubes do continente.' },
  { flag: '⭐', href: '/bolao-champions-league', nameKey: 'home.comp_4_name', name: 'Champions League 2026', descKey: 'home.comp_4_desc', desc: 'A maior competição de clubes do mundo reúne os gigantes do futebol europeu. Monte um bolão da Champions e dispute com amigos.' },
  { flag: '🔴', href: '/bolao-carioca', nameKey: 'home.comp_5_name', name: 'Campeonato Carioca 2026', descKey: 'home.comp_5_desc', desc: 'Flamengo, Vasco, Botafogo e Fluminense no mesmo campeonato. Crie um bolão do Carioca e veja quem conhece mais o futebol carioca.' },
  { flag: '⚫', href: '/bolao-paulista', nameKey: 'home.comp_6_name', name: 'Campeonato Paulista 2026', descKey: 'home.comp_6_desc', desc: 'O maior campeonato estadual do Brasil. Corinthians, Palmeiras, Santos e São Paulo brigando pelo Paulistão — crie seu bolão.' },
  { flag: '🟢', href: '/bolao-estaduais', nameKey: 'home.comp_7_name', name: 'Estaduais 2026', descKey: 'home.comp_7_desc', desc: 'Gaúcho, Mineiro, Baiano, Pernambucano e mais. Crie bolões para o campeonato da sua região e represente o seu estado.' },
];

// TODO: adicionar simuladores de Copa do Brasil e Champions League quando
// prontos (ver BACKLOG_APP.md — "Novos simuladores de competição").
const SIM_CARDS = [
  { to: '/simulador-copa-do-mundo-2026', emoji: '🌍', from: 'from-blue-900/80', via: 'to-blue-800/60', border: 'border-blue-700/40', featured: true, nameKey: 'home.sim_0_name', name: 'Copa do Mundo 2026', descKey: 'home.sim_0_desc', desc: '48 seleções, 12 grupos, 104 jogos. Simule o bracket completo da Copa do Mundo FIFA 2026.' },
  { to: '/simulador-libertadores-2026', emoji: '🏆', from: 'from-green-900/80', via: 'to-green-800/60', border: 'border-green-700/40', featured: false, nameKey: 'home.sim_1_name', name: 'Libertadores 2026', descKey: 'home.sim_1_desc', desc: '32 clubes, 8 grupos. Simule a maior competição de clubes da América do Sul.' },
  { to: '/simulador-copa-sudamericana-2026', emoji: '⚽', from: 'from-red-900/80', via: 'to-red-800/60', border: 'border-red-700/40', featured: false, nameKey: 'home.sim_2_name', name: 'Copa Sudamericana 2026', descKey: 'home.sim_2_desc', desc: '32 clubes disputam a segunda maior competição continental sul-americana.' },
];

// Seção "Times e Competições do Mundo" — alinhada à internacionalização.
// TODO: cadastrar competições de outros países e competições femininas
// no catálogo (ver BACKLOG_APP.md). Hoje o catálogo cobre sobretudo o Brasil.
const WORLD_ITEMS = [
  { emoji: '🌎', title: 'América do Sul', desc: 'Brasileirão, Libertadores, Sudamericana, Copa do Brasil e os estaduais — o coração do futebol sul-americano.' },
  { emoji: '🇪🇺', title: 'Europa', desc: 'Champions League, Premier League, La Liga e as grandes ligas europeias.' },
  { emoji: '🏆', title: 'Copa do Mundo', desc: 'A maior competição entre seleções — 48 seleções na Copa de 2026.' },
  { emoji: '🌍', title: 'Seleções nacionais', desc: 'Eliminatórias e torneios continentais de seleções dos cinco continentes.' },
  { emoji: '⚽', title: 'Futebol feminino', desc: 'O futebol feminino cresce pelo mundo — chegando em breve ao Bolão Elite.' },
  { emoji: '📈', title: 'Sempre crescendo', desc: 'Novas competições e países entram no catálogo continuamente.' },
];

export function Landing() {
  const { t } = useTranslation('landing');
  const hk = (suffix: string, def: string) => t(`home.${suffix}`, def);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [landingBanner, setLandingBanner] = useState<BannerConfig | null>(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const { googleVerificationCode } = usePublicConfig();
  const { limits } = usePublicFreeLimits();
  const location = useLocation();
  const landingJsonLd = useMemo(() => buildLandingJsonLd(limits), [limits]);

  useEffect(() => {
    bannersApi.getActive()
      .then((data) => setLandingBanner(data.landing))
      .catch(() => {}); // silencioso — banner é não-crítico
  }, []);

  // Se acessado pelo subdomínio app., redirecionar para o domínio principal
  useEffect(() => {
    if (isSubdomainEnabled() && isOnAppSubdomain()) {
      navigateToSite('/');
    }
  }, []);

  // Hash-scroll: quando o usuário chega na home via /#secao (vindo do menu
  // do PublicHeader em outra página), rola suavemente até a seção. O timeout
  // dá tempo das seções montarem antes do scroll.
  useEffect(() => {
    if (!location.hash) return;
    const target = location.hash;
    const t = setTimeout(() => {
      document.querySelector(target)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 120);
    return () => clearTimeout(t);
  }, [location.hash]);

  const loginUrl = isSubdomainEnabled() ? `https://${getAppDomain()}/login` : '/login';

  const seo = useLocalizedSeo('home');

  return (
    <div className="min-h-screen bg-white">
      {!bannerDismissed && (
        <AnnouncementBanner config={landingBanner} onDismiss={() => setBannerDismissed(true)} />
      )}
      <SEO
        title={hk('seo_title', 'Bolão Elite – Bolão Copa do Mundo 2026 | Bolão de Futebol Online Grátis')}
        description={hk('seo_desc', 'Crie seu bolão da Copa do Mundo 2026 e de outros campeonatos! Palpites em Brasileirão, Copa do Brasil, Libertadores, Carioca, Paulista, Gaúcho e mais. Ranking em tempo real. Participação 100% gratuita!')}
        keywords={hk('seo_keywords', 'bolão Copa do Mundo 2026, bolão Copa do Mundo 2026 grátis, como fazer bolão Copa do Mundo 2026, bolão Copa do Mundo com amigos, bolão de futebol online, criar bolão futebol grátis, bolão futebol com amigos, sistema de bolão futebol, bolão Brasileirão 2026, bolão Campeonato Brasileiro 2026, bolão Copa do Brasil 2026, bolão Libertadores 2026, bolão Sul-americana 2026, bolão Champions League 2026, bolão Campeonato Carioca 2026, bolão Campeonato Paulista 2026, bolão Campeonato Gaúcho 2026, bolão Campeonato Mineiro 2026, bolão estadual 2026, palpites futebol online, ranking bolão futebol, bolão grátis 2026, bolão entre amigos, bolão online gratuito, plano gratuito bolão futebol, limite bolão gratuito, bolão futebol com premiação')}
        canonical={seo.canonical}
        ogUrl={seo.ogUrl}
        locale={seo.locale}
        alternates={seo.alternates}
        jsonLd={landingJsonLd}
        googleVerificationCode={googleVerificationCode}
      />

      <PublicHeader />

      <main>

      {/* ─── HERO ─── */}
      <section className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-primary-900 overflow-hidden">
        {/* Decoração de fundo */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-32 -right-32 w-96 h-96 bg-white/5 rounded-full" />
          <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-white/5 rounded-full" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-white/[0.03] rounded-full" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32 text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-yellow-400/20 backdrop-blur-sm border border-yellow-300/40 rounded-full px-4 py-1.5 mb-8">
            <span className="text-2xl">🏆</span>
            <span className="text-yellow-200 text-sm font-semibold">{hk('hero_badge', 'Bolão Copa do Mundo 2026 já disponível!')}</span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-tight tracking-tight">
            {hk('hero_h1_line1', 'Bolão de futebol online')}<br className="hidden sm:block" />{' '}
            <span className="text-yellow-300">{hk('hero_h1_line2', 'Copa do Mundo 2026 e mais')}</span>
          </h1>

          <p className="mt-6 text-lg sm:text-xl text-white/80 max-w-2xl mx-auto leading-relaxed">
            <Trans i18nKey="home.hero_subtitle" t={t}>
              Crie seu bolão privado com amigos <span className="text-white font-semibold">ou entre em bolões públicos</span> abertos — sem precisar de convite. Copa do Mundo 2026, Brasileirão, Copa do Brasil, Libertadores e muito mais.
            </Trans>
          </p>

          <div className="mt-10 flex justify-center">
            <a
              href={loginUrl}
              className="px-10 py-4 text-base font-bold text-primary-700 bg-white rounded-xl shadow-lg hover:bg-primary-50 transition-all transform hover:scale-105 active:scale-100"
            >
              {hk('hero_cta', 'Começar Agora — é grátis!')}
            </a>
          </div>

          {/* Stats rápidos */}
          <div className="mt-16 grid grid-cols-3 gap-8 max-w-lg mx-auto">
            {HERO_STATS.map((stat) => (
              <div key={stat.labelKey} className="text-center">
                <div className="text-2xl sm:text-3xl font-extrabold text-white">{stat.value}</div>
                <div className="mt-1 text-sm text-white/60">{t(stat.labelKey, stat.labelDefault)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <AdSlot slotName="landing_hero_below" format="horizontal" area="public" className="max-w-5xl mx-auto px-4 py-6" />

      {/* ─── COMPETIÇÕES ─── */}
      <section id="competicoes" className="py-16 sm:py-20 bg-white scroll-mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-primary-600 text-sm font-semibold uppercase tracking-widest">{hk('comp_eyebrow', 'Campeonatos')}</span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold text-gray-900">
              {hk('comp_h2', 'Bolões para Toda Competição')}
            </h2>
            <p className="mt-4 text-gray-500 max-w-2xl mx-auto">
              {hk('comp_intro', 'Do bolão da Copa do Mundo 2026 ao campeonato do seu estado: crie bolões de futebol online para qualquer competição. Dados atualizados automaticamente — sem trabalho manual.')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {COMPETITIONS.map((comp) => (
              <Link
                key={comp.href}
                to={comp.href}
                className="relative bg-gray-50 border border-gray-100 rounded-2xl p-5 hover:border-primary-200 hover:bg-primary-50/30 transition-all group block"
              >
                <div className="text-3xl mb-3">{comp.flag}</div>
                <h3 className="text-sm font-bold text-gray-900 mb-1.5 group-hover:text-primary-700 transition-colors">
                  {t(comp.nameKey, comp.name)}
                </h3>
                <p className="text-xs text-gray-500 leading-relaxed">{t(comp.descKey, comp.desc)}</p>
              </Link>
            ))}
          </div>

          <p className="mt-8 text-center text-sm text-gray-600">
            {hk('comp_footer', 'Novos campeonatos adicionados regularmente. Sem limite de bolões por competição.')}
          </p>
        </div>
      </section>

      {/* ─── COMO FUNCIONA ─── */}
      <section id="como-funciona" className="py-20 sm:py-28 bg-white scroll-mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="text-primary-600 text-sm font-semibold uppercase tracking-widest">{hk('how_eyebrow', 'Passo a passo')}</span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold text-gray-900">{hk('how_h2', 'Como Funciona')}</h2>
            <p className="mt-4 text-gray-500 max-w-xl mx-auto">
              {hk('how_intro', 'Duas formas de jogar — você escolhe. Em poucos minutos já está disputando.')}
            </p>
          </div>

          {/* Dois caminhos */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-3xl mx-auto mb-12">
            {/* Caminho 1: Bolão Privado */}
            <div className="bg-primary-50 border-2 border-primary-200 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 bg-primary-600 text-white rounded-xl flex items-center justify-center shrink-0">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <div>
                  <span className="block text-xs text-primary-600 font-semibold uppercase tracking-wide mb-0.5">{hk('how_opt1_label', 'Opção 1')}</span>
                  <h3 className="text-base font-bold text-gray-900 leading-tight">{hk('how_opt1_title', 'Crie seu bolão privado')}</h3>
                </div>
              </div>
              <ol className="space-y-3">
                <li className="flex items-start gap-3 text-sm text-gray-700">
                  <span className="w-6 h-6 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">1</span>
                  <span>{hk('how_opt1_step1', 'Dê um nome, escolha a competição e defina as regras de pontuação.')}</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-gray-700">
                  <span className="w-6 h-6 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">2</span>
                  <span>{hk('how_opt1_step2', 'Compartilhe o link de convite pelo WhatsApp ou Instagram. Amigos entram com um clique.')}</span>
                </li>
              </ol>
            </div>

            {/* Caminho 2: Bolão Público */}
            <div className="bg-emerald-50 border-2 border-emerald-200 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 bg-emerald-600 text-white rounded-xl flex items-center justify-center shrink-0">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <span className="block text-xs text-emerald-600 font-semibold uppercase tracking-wide mb-0.5">{hk('how_opt2_label', 'Opção 2 — Novo!')}</span>
                  <h3 className="text-base font-bold text-gray-900 leading-tight">{hk('how_opt2_title', 'Entre em um bolão público')}</h3>
                </div>
              </div>
              <ol className="space-y-3">
                <li className="flex items-start gap-3 text-sm text-gray-700">
                  <span className="w-6 h-6 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">1</span>
                  <span>{hk('how_opt2_step1', 'Explore a lista de bolões públicos abertos na plataforma — sem precisar de convite.')}</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-gray-700">
                  <span className="w-6 h-6 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">2</span>
                  <span>{hk('how_opt2_step2', 'Clique em "Participar" e pronto. Você já está disputando com outros fãs de futebol.')}</span>
                </li>
              </ol>
            </div>
          </div>

          {/* Divisor: etapas em comum */}
          <div className="flex items-center gap-4 max-w-3xl mx-auto mb-10">
            <div className="flex-1 border-t border-gray-200" />
            <span className="text-sm text-gray-400 font-medium px-2 whitespace-nowrap">{hk('how_divider', 'Em ambos os casos')}</span>
            <div className="flex-1 border-t border-gray-200" />
          </div>

          {/* Steps comuns */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 max-w-3xl mx-auto">
            {[
              {
                step: '03',
                icon: (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                ),
                title: hk('how_step3_title', 'Faça seus Palpites'),
                desc: hk('how_step3_desc', 'Palpite o placar de cada partida antes do apito inicial. Depois não dá mais!'),
              },
              {
                step: '04',
                icon: (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                ),
                title: hk('how_step4_title', 'Acompanhe o Ranking'),
                desc: hk('how_step4_desc', 'Veja sua pontuação e a de todos os participantes atualizada em tempo real.'),
              },
            ].map((item) => (
              <div key={item.step} className="relative flex flex-col items-center text-center p-6">
                <div className="absolute -top-2 -left-2 w-8 h-8 bg-primary-100 text-primary-600 text-xs font-extrabold rounded-full flex items-center justify-center">
                  {item.step}
                </div>
                <div className="w-16 h-16 bg-primary-50 text-primary-600 rounded-2xl flex items-center justify-center mb-5 shadow-sm">
                  {item.icon}
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <AdSlot slotName="landing_content_between" format="horizontal" area="public" className="max-w-5xl mx-auto px-4 py-6" />

      {/* ─── FUNCIONALIDADES ─── */}
      <section id="funcionalidades" className="py-20 sm:py-28 bg-gradient-to-br from-primary-50 to-white scroll-mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-primary-600 text-sm font-semibold uppercase tracking-widest">{hk('feat_eyebrow', 'Recursos')}</span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold text-gray-900">{hk('feat_h2', 'Tudo que você precisa')}</h2>
            <p className="mt-4 text-gray-500 max-w-xl mx-auto">
              {hk('feat_intro', 'O Bolão Elite foi pensado para ser simples de usar e cheio de recursos para tornar a disputa mais emocionante.')}
            </p>
          </div>

          {/* Destaque: Bolões Públicos — nova funcionalidade */}
          <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 rounded-2xl p-6 sm:p-8 mb-6 text-white flex flex-col sm:flex-row items-start gap-5">
            <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center shrink-0">
              <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <span className="text-xs font-bold uppercase tracking-widest text-emerald-200 mb-1 block">{hk('feat_highlight_badge', 'Novidade')}</span>
              <h3 className="text-lg sm:text-xl font-bold mb-2">{hk('feat_highlight_title', 'Bolões Públicos — participe sem precisar de convite')}</h3>
              <p className="text-emerald-100 text-sm leading-relaxed max-w-2xl">
                {hk('feat_highlight_desc', 'Agora você pode explorar bolões abertos para toda a comunidade e entrar com um clique — sem precisar de link de convite. Perfeito para quem quer disputar com outros fãs ou experimentar o sistema antes de criar o próprio bolão.')}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                ),
                title: hk('feat_1_title', 'Múltiplos Bolões'),
                desc: hk('feat_1_desc', 'Participe de vários bolões ao mesmo tempo — bolões privados com amigos e bolões públicos da comunidade.'),
              },
              {
                icon: (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                ),
                title: hk('feat_2_title', 'Ranking em Tempo Real'),
                desc: hk('feat_2_desc', 'Classificação atualizada automaticamente assim que os jogos terminam. Sem esperar.'),
              },
              {
                icon: (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                ),
                title: hk('feat_3_title', 'Notificações Push'),
                desc: hk('feat_3_desc', 'Receba alertas antes das partidas para não esquecer de registrar seus palpites.'),
              },
              {
                icon: (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                ),
                title: hk('feat_4_title', 'Pontuação Transparente'),
                desc: hk('feat_4_desc', 'Regras claras de pontuação exibidas para todos. Placar exato vale mais, vencedor correto garante pontos.'),
              },
              {
                icon: (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                  </svg>
                ),
                title: hk('feat_5_title', 'Convites por Link'),
                desc: hk('feat_5_desc', 'Gere um link único para o seu bolão e compartilhe no WhatsApp, Instagram ou onde quiser.'),
              },
              {
                icon: (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ),
                title: hk('feat_6_title', 'Competições Oficiais'),
                desc: hk('feat_6_desc', 'Copa do Mundo 2026, Brasileirão, Copa do Brasil, Libertadores, Champions League, Carioca, Paulista, Gaúcho e mais. Dados atualizados automaticamente.'),
              },
            ].map((feat) => (
              <div
                key={feat.title}
                className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md hover:border-primary-100 transition-all group"
              >
                <div className="w-12 h-12 bg-primary-50 text-primary-600 rounded-xl flex items-center justify-center mb-4 group-hover:bg-primary-100 transition-colors">
                  {feat.icon}
                </div>
                <h3 className="text-base font-bold text-gray-900 mb-2">{feat.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{feat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── PONTUAÇÃO ─── */}
      <section id="pontuacao" className="py-20 sm:py-28 bg-white scroll-mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-primary-600 text-sm font-semibold uppercase tracking-widest">{hk('pts_eyebrow', 'Como pontuar')}</span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold text-gray-900">{hk('pts_h2', 'Sistema de Pontuação')}</h2>
            <p className="mt-4 text-gray-500 max-w-xl mx-auto">
              {hk('pts_intro', 'Simples e justo. Quanto mais preciso o seu palpite, mais pontos você ganha.')}
            </p>
          </div>

          {/* 3 sistemas de pontuação */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-12">
            {/* Regra A */}
            <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-4">
                <span className="w-7 h-7 bg-gray-200 text-gray-700 rounded-full flex items-center justify-center text-xs font-extrabold">A</span>
                <span className="text-sm font-bold text-gray-700">{hk('pts_a_name', 'Regra Simples')}</span>
              </div>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex justify-between items-center">
                  <span>{hk('pts_row_exact', 'Placar exato')}</span>
                  <span className="font-bold text-gray-900">1 pt</span>
                </li>
                <li className="flex justify-between items-center text-gray-600">
                  <span>{hk('pts_row_other_results', 'Outros resultados')}</span>
                  <span className="font-bold">0 pts</span>
                </li>
              </ul>
              <p className="mt-4 text-xs text-gray-600">{hk('pts_a_note', 'Ideal para quem quer manter simples.')}</p>
            </div>

            {/* Regra B — destaque */}
            <div className="relative bg-gradient-to-br from-primary-600 to-primary-800 rounded-2xl p-6 text-white shadow-lg overflow-hidden">
              <div className="absolute top-0 right-0 w-28 h-28 bg-white/5 rounded-full -translate-y-8 translate-x-8" />
              <div className="flex items-center gap-2 mb-4">
                <span className="w-7 h-7 bg-white/20 text-white rounded-full flex items-center justify-center text-xs font-extrabold">B</span>
                <span className="text-sm font-bold">{hk('pts_b_name', 'Regra Intermediária')}</span>
                <span className="ml-auto text-xs bg-yellow-400 text-yellow-900 font-bold px-2 py-0.5 rounded-full">{hk('pts_b_badge', 'Popular')}</span>
              </div>
              <ul className="space-y-2 text-sm">
                <li className="flex justify-between items-center">
                  <span className="text-white/80">{hk('pts_row_exact', 'Placar exato')}</span>
                  <span className="font-bold">3 pts</span>
                </li>
                <li className="flex justify-between items-center">
                  <span className="text-white/80">{hk('pts_row_winner', 'Vencedor/empate')}</span>
                  <span className="font-bold">1 pt</span>
                </li>
                <li className="flex justify-between items-center text-white/60">
                  <span>{hk('pts_row_others', 'Outros')}</span>
                  <span className="font-bold">0 pts</span>
                </li>
              </ul>
              <p className="mt-4 text-xs text-white/70">{hk('pts_b_note', 'Equilíbrio entre dificuldade e emoção.')}</p>
            </div>

            {/* Regra C */}
            <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-4">
                <span className="w-7 h-7 bg-gray-800 text-white rounded-full flex items-center justify-center text-xs font-extrabold">C</span>
                <span className="text-sm font-bold text-gray-700">{hk('pts_c_name', 'Regra Avançada')}</span>
              </div>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex justify-between items-center">
                  <span>{hk('pts_row_exact', 'Placar exato')}</span>
                  <span className="font-bold text-gray-900">10 pts</span>
                </li>
                <li className="flex justify-between items-center">
                  <span>{hk('pts_row_winner', 'Vencedor/empate')}</span>
                  <span className="font-bold text-gray-900">5 pts</span>
                </li>
                <li className="flex justify-between items-center">
                  <span>{hk('pts_row_team_goals', 'Gols de um time')}</span>
                  <span className="font-bold text-gray-900">2 pts</span>
                </li>
                <li className="flex justify-between items-center text-gray-600">
                  <span>{hk('pts_row_others', 'Outros')}</span>
                  <span className="font-bold">0 pts</span>
                </li>
              </ul>
              <p className="mt-4 text-xs text-gray-600">{hk('pts_c_note', 'Para os mais competitivos.')}</p>
            </div>
          </div>

          {/* Veja na prática — exemplo que mata a dúvida do "a pontuação é cumulativa?" */}
          <div className="max-w-2xl mx-auto bg-gradient-to-br from-primary-50 to-white border border-primary-100 rounded-2xl p-6 mb-10">
            <div className="text-center">
              <span className="text-primary-600 text-xs font-semibold uppercase tracking-widest">{hk('pts_ex_title', 'Veja na prática')}</span>
              <p className="mt-2 text-sm text-gray-700">
                {hk('pts_ex_scenario', 'Você palpitou 1 × 0 e o jogo terminou 2 × 0:')}
              </p>
            </div>
            <div className="mt-5 grid grid-cols-3 gap-3">
              {[
                { letter: 'A', result: hk('pts_ex_a_result', '0 pts'), highlight: false },
                { letter: 'B', result: hk('pts_ex_b_result', '1 pt'), highlight: false },
                { letter: 'C', result: hk('pts_ex_c_result', '5 pts'), highlight: true },
              ].map((r) => (
                <div
                  key={r.letter}
                  className={`rounded-xl p-3 text-center border ${r.highlight ? 'bg-primary-600 border-primary-600 text-white' : 'bg-white border-gray-200'}`}
                >
                  <div className={`text-xs ${r.highlight ? 'text-white/80' : 'text-gray-500'}`}>{hk('pts_ex_rule', 'Regra')} {r.letter}</div>
                  <div className={`mt-1 text-lg font-extrabold ${r.highlight ? 'text-white' : 'text-gray-900'}`}>{r.result}</div>
                </div>
              ))}
            </div>
            <p className="mt-5 text-xs text-gray-700 bg-amber-50 border border-amber-100 rounded-xl p-3 leading-relaxed">
              ⚠️ {hk('pts_ex_note', 'As faixas não se somam — vale só a melhor que você atingiu. Por isso, na Regra C são 5 pontos (você acertou o vencedor), e não 7.')}
            </p>
          </div>

          <p className="text-center text-sm text-gray-600 mb-10">
            {hk('pts_footer', 'A regra de pontuação é escolhida pelo criador do bolão no momento da criação.')}
          </p>

          {/* Critérios de desempate */}
          <div className="max-w-xl mx-auto bg-gray-50 rounded-2xl p-6 border border-gray-100">
            <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
              <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {hk('pts_tiebreak_h3', 'Critérios de Desempate')}
            </h3>
            <ol className="space-y-2">
              {[
                hk('pts_tiebreak_0', 'Total de pontos'),
                hk('pts_tiebreak_1', 'Número de placares exatos'),
                hk('pts_tiebreak_2', 'Número de vencedores corretos'),
                hk('pts_tiebreak_3', 'Menor diferença agregada de gols'),
                hk('pts_tiebreak_4', 'Ordem alfabética por nome'),
              ].map((criteria, i) => (
                <li key={i} className="flex items-center gap-3 text-sm text-gray-700">
                  <span className="w-6 h-6 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center text-xs font-bold shrink-0">
                    {i + 1}
                  </span>
                  {criteria}
                </li>
              ))}
            </ol>
          </div>

          {/* FAQ de pontuação — detalha as regras com exemplos */}
          <ScoringFAQ className="max-w-2xl mx-auto mt-12" />
        </div>
      </section>

      {/* ─── SIMULADORES DE COPAS ─── */}
      <section id="simuladores" className="py-16 sm:py-20 bg-gradient-to-br from-gray-900 to-gray-800 text-white scroll-mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="text-yellow-400 text-sm font-semibold uppercase tracking-widest">{hk('sim_eyebrow', 'Novo')}</span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold">{hk('sim_h2', 'Simuladores de Copas')}</h2>
            <p className="mt-4 text-gray-400 max-w-xl mx-auto">
              {hk('sim_intro', 'Monte seu bracket, escolha os classificados de cada grupo e simule o caminho até a grande final.')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 max-w-4xl mx-auto">
            {SIM_CARDS.map((sim) => (
              <Link
                key={sim.to}
                to={sim.to}
                className={`relative bg-gradient-to-br ${sim.from} ${sim.via} border ${sim.border} rounded-2xl p-6 hover:border-yellow-400/60 transition-all group block overflow-hidden`}
              >
                {sim.featured && (
                  <div className="absolute top-2 right-2 text-xs bg-yellow-400 text-yellow-900 font-bold px-2 py-0.5 rounded-full">
                    {hk('sim_featured_badge', 'Destaque')}
                  </div>
                )}
                <div className="text-3xl mb-3">{sim.emoji}</div>
                <h3 className="text-base font-bold text-white mb-1.5 group-hover:text-yellow-300 transition-colors">
                  {t(sim.nameKey, sim.name)}
                </h3>
                <p className="text-xs text-gray-300 leading-relaxed">
                  {t(sim.descKey, sim.desc)}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ─── MONTE SUA ESCALAÇÃO ─── */}
      <section id="escalar-selecao" className="py-16 sm:py-20 bg-gradient-to-br from-green-900 to-green-950 text-white scroll-mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="text-yellow-400 text-sm font-semibold uppercase tracking-widest">{hk('esc_eyebrow', 'Ferramentas')}</span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold leading-tight">
              {hk('esc_h2', 'Monte Sua Escalação')}
            </h2>
            <p className="mt-4 text-green-200 text-base max-w-xl mx-auto">
              {hk('esc_intro', 'Escolha a formação tática, escale os 11 titulares e compartilhe com seus amigos. Grátis, sem cadastro.')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {/* Card: Seleção Brasileira */}
            <Link
              to="/escalar-selecao-brasileira-2026"
              className="group bg-white/10 hover:bg-white/15 border border-white/20 rounded-2xl p-6 transition-all hover:shadow-lg"
            >
              <div className="text-3xl mb-3">🇧🇷</div>
              <h3 className="text-lg font-bold text-white mb-2">{hk('esc_0_name', 'Seleção Brasileira 2026')}</h3>
              <p className="text-sm text-green-200 mb-4">
                {hk('esc_0_desc', 'Escale os 11 titulares da Seleção para a Copa do Mundo 2026.')}
              </p>
              <span className="inline-flex items-center gap-1 text-yellow-400 text-sm font-semibold group-hover:gap-2 transition-all">
                {hk('esc_cta', 'Escalar agora →')}
              </span>
            </Link>

            {/* Card: Seu Time do Brasileirão */}
            <Link
              to="/escale-seu-time"
              className="group bg-white/10 hover:bg-white/15 border border-white/20 rounded-2xl p-6 transition-all hover:shadow-lg"
            >
              <div className="text-3xl mb-3">⚽</div>
              <h3 className="text-lg font-bold text-white mb-2">{hk('esc_1_name', 'Seu Time do Brasileirão')}</h3>
              <p className="text-sm text-green-200 mb-4">
                {hk('esc_1_desc', 'Escolha seu time do coração e monte a escalação ideal.')}
              </p>
              <span className="inline-flex items-center gap-1 text-yellow-400 text-sm font-semibold group-hover:gap-2 transition-all">
                {hk('esc_cta', 'Escalar agora →')}
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* ─── TIMES E COMPETIÇÕES DO MUNDO ─── */}
      <section id="mundo" className="py-16 sm:py-20 bg-white scroll-mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="text-primary-600 text-sm font-semibold uppercase tracking-widest">
              {hk('world_eyebrow', 'Futebol do mundo todo')}
            </span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold text-gray-900">
              {hk('world_h2', 'Times e Competições do Mundo')}
            </h2>
            <p className="mt-4 text-gray-600 max-w-2xl mx-auto">
              {hk('world_intro', 'Do Brasileirão à Champions League, da Copa do Mundo às Eliminatórias — crie bolões e simule os campeonatos que você acompanha, onde quer que o futebol aconteça.')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl mx-auto">
            {WORLD_ITEMS.map((item, i) => (
              <div key={i} className="bg-gray-50 border border-gray-100 rounded-2xl p-6">
                <div className="text-3xl mb-3">{item.emoji}</div>
                <h3 className="text-base font-bold text-gray-900 mb-1.5">
                  {hk(`world_${i}_title`, item.title)}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {hk(`world_${i}_desc`, item.desc)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Baixe o App ─── */}
      <section id="app" className="py-20 sm:py-28 bg-gradient-to-br from-primary-700 via-primary-800 to-primary-900 text-white scroll-mt-16 relative overflow-hidden">
        {/* Detalhes decorativos */}
        <div className="absolute top-0 right-0 w-72 h-72 bg-white/5 rounded-full -mr-36 -mt-36" />
        <div className="absolute bottom-0 left-0 w-56 h-56 bg-white/5 rounded-full -ml-28 -mb-28" />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div className="text-center lg:text-left">
              <span className="inline-block text-primary-200 text-sm font-semibold uppercase tracking-widest mb-3">
                {hk('app_eyebrow', 'Disponível na Google Play')}
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold mb-4 leading-tight">
                {hk('app_h2', 'Leve seu bolão no bolso')}
              </h2>
              <p className="text-primary-100 text-base sm:text-lg mb-7 max-w-md mx-auto lg:mx-0">
                {hk('app_desc', 'Palpite de qualquer lugar, receba lembretes antes dos jogos, acompanhe o ranking ao vivo e desbloqueie conquistas — tudo no app oficial do Bolão Elite.')}
              </p>
              <div className="flex justify-center lg:justify-start">
                <PlayStoreBadge />
              </div>
              <p className="text-primary-200/70 text-xs mt-4">
                {hk('app_note', 'Grátis · Android · iOS em breve')}
              </p>
            </div>
            {/* Lista de destaques */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 sm:p-8 border border-white/15">
              <ul className="space-y-4">
                {[
                  hk('app_item_0', '🔔 Lembretes de jogos pra não perder o prazo de palpitar'),
                  hk('app_item_1', '📊 Ranking ao vivo e pontuação atualizada em tempo real'),
                  hk('app_item_2', '🏆 Conquistas e gamificação pra deixar tudo mais divertido'),
                  hk('app_item_3', '⚽ Simuladores de Copa, Libertadores e Sudamericana no celular'),
                  hk('app_item_4', '👥 Crie bolões privados e convide amigos por link'),
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-primary-50 text-sm sm:text-base">
                    <span className="flex-shrink-0">{item.slice(0, 2)}</span>
                    <span>{item.slice(2).trim()}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ─── FAQ ─── */}
      <section id="faq" className="py-20 sm:py-28 bg-gray-50 scroll-mt-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-primary-600 text-sm font-semibold uppercase tracking-widest">{hk('faq_eyebrow', 'Tire suas dúvidas')}</span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold text-gray-900">{hk('faq_h2', 'Perguntas Frequentes')}</h2>
            <p className="mt-4 text-gray-500 max-w-xl mx-auto">
              {hk('faq_intro', 'Respondemos as dúvidas mais comuns sobre o Bolão Elite.')}
            </p>
          </div>

          <div className="space-y-3">
            {[
              {
                q: hk('faq_0_q', 'O Bolão Elite é gratuito?'),
                a: t('home.faq_0_a', {
                  defaultValue: `O Bolão Elite tem plano gratuito e plano Premium. No plano gratuito você pode criar ${limits.maxPrivatePoolsCreated} bolão privado com até ${limits.maxPrivatePoolMembers} participantes e entrar em até ${limits.maxPrivatePoolsJoined} bolões privados como convidado, além de bolões públicos sem limite. O plano Premium remove todas as restrições: bolões privados ilimitados, participantes ilimitados e muito mais.`,
                  ...limits,
                }),
              },
              {
                q: hk('faq_1_q', 'Quais são os limites do plano gratuito?'),
                a: t('home.faq_1_a', {
                  defaultValue: `No plano gratuito você pode criar ${limits.maxPrivatePoolsCreated} bolão privado com até ${limits.maxPrivatePoolMembers} participantes e participar de até ${limits.maxPrivatePoolsJoined} bolões privados como convidado. Bolões públicos não têm limite de participação. O plano Premium desbloqueia bolões privados ilimitados e participantes ilimitados.`,
                  ...limits,
                }),
              },
              {
                q: t('home.faq_2_q', {
                  defaultValue: `No plano gratuito só consigo ter ${limits.maxPrivatePoolsCreated} bolão privado ao mesmo tempo?`,
                  ...limits,
                }),
                a: t('home.faq_2_a', {
                  defaultValue: `Isso mesmo! No plano gratuito você pode ter ${limits.maxPrivatePoolsCreated} bolão privado ativo simultaneamente. Se quiser criar um novo bolão para outra competição ou grupo de amigos, basta excluir o bolão anterior — e pronto, a vaga fica disponível. Quer criar bolões privados ilimitados sem essa limitação? O plano Premium libera tudo isso e muito mais.`,
                  ...limits,
                }),
              },
              {
                q: hk('faq_3_q', 'Como funciona o recurso de ocultar palpites antes de iniciar a partida?'),
                a: hk('faq_3_a', 'O criador do bolão pode ativar a opção de ocultar palpites antes do início de cada partida. Com isso, os participantes não conseguem ver os palpites uns dos outros até a partida começar, evitando influências e deixando o bolão mais justo. A opção pode ser definida ao criar o bolão ou ativada depois pelo menu de configurações (três pontinhos) do bolão.'),
              },
              {
                q: hk('faq_4_q', 'O Bolão Elite já tem a Copa do Mundo 2026?'),
                a: hk('faq_4_a', 'Sim! O Bolão Elite já suporta a Copa do Mundo 2026. Crie seu bolão agora e deixe tudo pronto para quando os jogos começarem em junho de 2026. Os palpites são bloqueados automaticamente no apito inicial de cada partida.'),
              },
              {
                q: hk('faq_5_q', 'Quais campeonatos posso usar no meu bolão?'),
                a: hk('faq_5_a', 'O Bolão Elite suporta os principais campeonatos: Copa do Mundo 2026, Campeonato Brasileiro, Copa do Brasil, Libertadores, Champions League, Campeonato Carioca, Campeonato Paulista, Campeonato Gaúcho, Campeonato Mineiro e outros campeonatos estaduais e internacionais.'),
              },
              {
                q: hk('faq_6_q', 'O que são bolões públicos?'),
                a: hk('faq_6_a', 'Bolões públicos são bolões abertos a qualquer usuário da plataforma, sem precisar de link de convite. Basta acessar a seção "Bolões Públicos", escolher um bolão e clicar em "Participar". Você compete com outros fãs de futebol da comunidade e pode sair a qualquer momento.'),
              },
              {
                q: hk('faq_7_q', 'Como funciona a pontuação do bolão?'),
                a: hk('faq_7_a', 'O Bolão Elite oferece 3 regras de pontuação. Na Regra B (a mais popular): acertar o placar exato vale 3 pontos e acertar o vencedor ou empate vale 1 ponto. A regra é definida pelo criador do bolão.'),
              },
              {
                q: hk('faq_8_q', 'Até quando posso fazer meu palpite?'),
                a: hk('faq_8_a', 'O criador do bolão define um tempo de bloqueio antes do início da partida: 10, 30 ou 60 minutos. Após esse prazo, os palpites são bloqueados automaticamente, mesmo que o jogo ainda não tenha começado.'),
              },
              {
                q: hk('faq_9_q', 'Preciso baixar algum aplicativo?'),
                a: hk('faq_9_a', 'Não! O Bolão Elite funciona diretamente no navegador do seu celular ou computador. Você também pode adicioná-lo à tela inicial do smartphone para acesso rápido, como se fosse um app nativo.'),
              },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-gray-900 text-sm sm:text-base">{item.q}</span>
                  <svg
                    className={`w-5 h-5 text-primary-600 shrink-0 ml-4 transition-transform ${openFaq === i ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-4">
                    <p className="text-gray-600 text-sm leading-relaxed">{item.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── CTA FINAL ─── */}
      <section className="py-20 sm:py-28 bg-gradient-to-br from-primary-600 to-primary-800 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-20 -right-20 w-72 h-72 bg-white/5 rounded-full" />
          <div className="absolute -bottom-16 -left-16 w-64 h-64 bg-white/5 rounded-full" />
        </div>
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-4">
            {hk('final_cta_h2', 'Pronto para a Copa do Mundo 2026?')}
          </h2>
          <p className="text-white/70 text-lg mb-10">
            {hk('final_cta_text', 'Crie seu bolão privado com amigos ou entre em bolões públicos abertos da comunidade. Copa do Mundo, Brasileirão, estaduais — tudo em um só lugar, 100% gratuito.')}
          </p>
          <div className="flex justify-center">
            <a
              href={loginUrl}
              className="px-10 py-4 text-base font-bold text-primary-700 bg-white rounded-xl shadow-lg hover:bg-primary-50 transition-all transform hover:scale-105 active:scale-100"
            >
              {hk('hero_cta', 'Começar Agora — é grátis!')}
            </a>
          </div>
        </div>
      </section>

      <AdSlot slotName="landing_footer_above" format="horizontal" area="public" className="max-w-5xl mx-auto px-4 py-6" />

      </main>

      <PublicFooter />
    </div>
  );
}
